import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button } from "@/components/ui/Button";

interface Props {
  children: ReactNode;
  /** Heading shown instead of the crashed subtree. */
  title?: string;
  /** Extra hint under the heading — say what the user loses and what still works. */
  hint?: string;
  /** Storage keys wiped by the „Zresetuj ustawienia widoku" button. Omit to hide it. */
  resetKeys?: string[];
  /** Tag prefixed to the console error, so a report from a user can be traced to a subtree. */
  label?: string;
}

interface State {
  error: Error | null;
  info: ErrorInfo | null;
}

/**
 * Contains a crash to one subtree.
 *
 * Without a boundary, a throw anywhere under the router — including inside a `useEffect`,
 * which is where the Leaflet layers build — bubbles to the route's `errorElement` and
 * replaces the WHOLE application with the generic "Błąd" page. The user then has no data,
 * no message, and nothing to report beyond "nie otwiera się".
 *
 * This keeps the rest of the page alive and puts the actual error text on screen, where a
 * user can copy it into a ticket. It is deliberately NOT silent.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, info: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.setState({ info });
    // Keep the full stack in the console even though the panel shows a trimmed version —
    // a support session usually starts with "wklej co jest w konsoli".
    console.error(`[ErrorBoundary${this.props.label ? ` ${this.props.label}` : ""}]`, error, info.componentStack);
  }

  private retry = () => this.setState({ error: null, info: null });

  private resetView = () => {
    for (const key of this.props.resetKeys ?? []) {
      try {
        localStorage.removeItem(key);
      } catch {
        // Storage unavailable (private mode) — nothing persisted, nothing to clear.
      }
    }
    // A full reload is the honest way back: the crashed subtree holds imperative state
    // (Leaflet map instances, panes, layer refs) that a re-render would not rebuild cleanly.
    window.location.reload();
  };

  render() {
    const { error, info } = this.state;
    if (!error) return this.props.children;

    const { title = "Ta część strony się nie wczytała", hint, resetKeys } = this.props;

    return (
      <div className="my-4 rounded border border-red-300 bg-red-50 p-4 dark:border-red-800 dark:bg-red-950/30">
        <p className="text-sm font-semibold text-red-700 dark:text-red-400">{title}</p>
        {hint && (
          <p className="mt-1 text-xs text-red-700/80 dark:text-red-400/80">{hint}</p>
        )}

        <p className="mt-3 font-mono text-[11px] break-words text-red-800 dark:text-red-300">
          {error.name}: {error.message}
        </p>

        <details className="mt-2">
          <summary className="cursor-pointer text-[11px] text-red-700/70 dark:text-red-400/70">
            Szczegóły techniczne (do zgłoszenia)
          </summary>
          <pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap rounded bg-white/60 p-2 font-mono text-[10px] text-red-900 dark:bg-black/30 dark:text-red-200">
            {error.stack ?? "(brak stack trace)"}
            {info?.componentStack ?? ""}
          </pre>
        </details>

        <div className="mt-3 flex flex-wrap gap-2">
          <Button size="sm" variant="ghost" onClick={this.retry}>
            Spróbuj ponownie
          </Button>
          {resetKeys && resetKeys.length > 0 && (
            <Button size="sm" variant="ghost" onClick={this.resetView}>
              Zresetuj ustawienia widoku i przeładuj
            </Button>
          )}
        </div>
      </div>
    );
  }
}
